/**
 * MKS-364 — flip a ticket's time-booking lock, with an optimistic board update.
 *
 * Same shape as `useAssignTaskMilestone` (onMutate snapshot + setQueryData;
 * onError rollback from context; onSettled invalidate), and the SAME single
 * cache key: `['board', projectId, 'internal']`. One key is enough for both
 * surfaces that show the toggle — `TaskDetailPage` deliberately joins into that
 * very board cache entry rather than owning a single-task query (see its file
 * header), so the full-page ticket view updates from this invalidation too.
 *
 * 'internal' is hard-coded rather than taken as an audience parameter: an
 * external actor is refused by the backend (403
 * EXTERNAL_FORBIDDEN_TIME_BOOKING_LOCK) and never sees the toggle, so an
 * external board cache can never hold a flip to apply.
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { setTaskTimeBookingLock, type BoardResponse } from '../services/api/board';

export interface SetTaskTimeBookingLockInput {
  projectId: string;
  taskId: number;
  locked: boolean;
}

interface MutationContext {
  previous: BoardResponse | undefined;
}

export function useSetTaskTimeBookingLock() {
  const queryClient = useQueryClient();

  return useMutation<
    { id: number; timeBookingLocked: boolean },
    Error,
    SetTaskTimeBookingLockInput,
    MutationContext
  >({
    mutationFn: (input) => setTaskTimeBookingLock(input.taskId, input.locked),
    onMutate: async (input) => {
      const key = ['board', input.projectId, 'internal'] as const;
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BoardResponse>(key);
      if (previous) {
        queryClient.setQueryData<BoardResponse>(
          key,
          applyOptimisticTimeBookingLock(previous, input.taskId, input.locked),
        );
      }
      return { previous };
    },
    onError: (_err, vars, context) => {
      // Restore the snapshot; surfacing the message is the caller's job via the
      // per-mutate `{ onError }` option, matching every sibling hook here.
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['board', vars.projectId, 'internal'], context.previous);
      }
    },
    onSettled: (_data, _err, input) => {
      queryClient.invalidateQueries({ queryKey: ['board', input.projectId, 'internal'] });
    },
  });
}

/** Pure helper — keeps the optimistic update testable on its own. */
export function applyOptimisticTimeBookingLock(
  board: BoardResponse,
  taskId: number,
  locked: boolean,
): BoardResponse {
  return {
    ...board,
    columns: board.columns.map((col) => ({
      ...col,
      tasks: col.tasks.map((t) => (t.id === taskId ? { ...t, timeBookingLocked: locked } : t)),
    })),
  };
}
